import type { Category, Item, ModifierGroup, StaffMenu } from "@luckymenu/core";

/**
 * What the Menu tab shows, worked out from `staff.catalog.listMenu`: sections
 * per category, search, counts and the price and state of a row. Pure, so
 * both staff apps render the same list.
 */

/** Lower case, Latin accents removed, spaces collapsed. Indian scripts are left as typed. */
export function normaliseSearch(text: string): string {
	return text
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/\s+/g, " ")
		.trim();
}

/**
 * Whether an item matches what was typed: every word appears in its name,
 * print name or description, or the query is the start of its short code.
 */
export function itemMatches(item: Item, query: string): boolean {
	const q = normaliseSearch(query);
	if (q === "") return true;
	if (item.shortCode?.toLowerCase().startsWith(q.replace(/ /g, ""))) return true;
	const text = normaliseSearch([item.name, item.namePrint ?? "", item.description ?? ""].join(" "));
	return q.split(" ").every((word) => text.includes(word));
}

export type MenuSection = {
	category: Category;
	/** In menu order, filtered by the search. */
	items: Item[];
	/** Every item of the category, ignoring the search. */
	total: number;
	/** Items of the category that are sold out. */
	soldOut: number;
};

/**
 * Categories in menu order with their items. With a search, only matching
 * items and the categories that have some; without, every category, empty
 * ones included.
 */
export function menuSections(menu: StaffMenu, query = ""): MenuSection[] {
	const searching = normaliseSearch(query) !== "";
	const byCategory = itemsByCategory(menu.items);
	const sections: MenuSection[] = [];
	for (const category of menu.categories) {
		const all = byCategory.get(category.id) ?? [];
		const items = searching ? all.filter((item) => itemMatches(item, query)) : all;
		if (searching && items.length === 0) continue;
		sections.push({
			category,
			items,
			total: all.length,
			soldOut: all.filter((i) => !i.isAvailable).length,
		});
	}
	return sections;
}

/** Items grouped by category, keeping menu order. */
export function itemsByCategory(items: readonly Item[]): Map<string, Item[]> {
	const map = new Map<string, Item[]>();
	for (const item of items) {
		const list = map.get(item.categoryId);
		if (list) list.push(item);
		else map.set(item.categoryId, [item]);
	}
	return map;
}

/** The price a row shows. `from` when the sizes have different prices ("from ₹120"). */
export function itemPrice(item: Item): { paise: number; from: boolean } {
	const prices = new Set(item.variants.map((v) => v.pricePaise));
	return { paise: item.basePricePaise, from: prices.size > 1 };
}

/**
 * How a row looks: `hidden` (not on the menu), `soldOut`, `someSoldOut` (a
 * size is sold out) or `available`.
 */
export type ItemState = "hidden" | "soldOut" | "someSoldOut" | "available";

export function itemState(item: Item, category?: Category): ItemState {
	if (!item.isActive || category?.isActive === false) return "hidden";
	if (!item.isAvailable) return "soldOut";
	if (item.variants.some((v) => !v.isAvailable)) return "someSoldOut";
	return "available";
}

/** Add-on groups an item offers, in its order. Unknown IDs are skipped. */
export function groupsOf(item: Item, groups: readonly ModifierGroup[]): ModifierGroup[] {
	const byId = new Map(groups.map((g) => [g.id, g]));
	return item.modifierGroupIds.flatMap((id) => byId.get(id) ?? []);
}

/** Items that offer an add-on group, in menu order. */
export function itemsWithGroup(menu: StaffMenu, groupId: string): Item[] {
	return menu.items.filter((i) => i.modifierGroupIds.includes(groupId));
}

/**
 * How an add-on group's choice reads: optional up to `max`, exactly `min`,
 * or from `min` to `max`. Apps translate `menu:groupRule.<kind>`.
 */
export function groupRule(group: Pick<ModifierGroup, "minSelect" | "maxSelect">): {
	kind: "optional" | "exactly" | "range";
	min: number;
	max: number;
} {
	const { minSelect: min, maxSelect: max } = group;
	if (min === 0) return { kind: "optional", min, max };
	if (min === max) return { kind: "exactly", min, max };
	return { kind: "range", min, max };
}
